import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import LandingPagesApi from 'libs/api/website/landingPages';
import AuthService from 'services/Auth';

const useLandingPages = () => {
  const [landingPages, setLandingPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({});
  const [shoppingCenters, setShoppingCenters] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const navigate = useNavigate();
  const paramsRef = useRef({});

  const checkAuth = useCallback(() => {
    const token = AuthService.getToken();
    if (!token) {
      message.error('You are not authorized. Please login.');
      navigate('/login');
      return false;
    }
    return true;
  }, [navigate]);

  const fetchLandingPages = useCallback(async (params = {}) => {
    if (!checkAuth()) return;
    setLoading(true);
    try {
      const response = await LandingPagesApi.list(params);
      if (response.data.success) {
        const rows = response.data.landing_pages || [];
        setLandingPages(rows);
        setTotal(response.data.pagination?.total_count || rows.length || 0);
      } else {
        setLandingPages([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching landing pages:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to load landing pages');
      }
    } finally {
      setLoading(false);
    }
  }, [checkAuth, navigate]);

  const fetchFilters = useCallback(async () => {
    if (!checkAuth()) return;
    setLoadingCompanies(true);
    try {
      const response = await LandingPagesApi.getFilters();
      const shoppingCentersRaw = response.data.shopping_centers || [];
      setShoppingCenters(shoppingCentersRaw);
    } catch (error) {
      console.error('Error fetching filters:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error('Failed to load filters');
      }
    } finally {
      setLoadingCompanies(false);
    }
  }, [checkAuth, navigate]);

  const fetchLandingPage = useCallback(async (id) => {
    if (!checkAuth()) return null;
    try {
      const response = await LandingPagesApi.show(id);
      if (response.data.success) {
        return response.data.landing_page;
      }
      return null;
    } catch (error) {
      console.error('Error fetching landing page:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to load landing page');
      }
      return null;
    }
  }, [checkAuth, navigate]);

  const createLandingPage = useCallback(async (data) => {
    if (!checkAuth()) return null;
    try {
      const response = await LandingPagesApi.create(data);
      

      if (response.status === 204 || response.status === 200 || response.data?.success) {
        return response.data?.landing_page || response.data || true;
      }
      return null;
    } catch (error) {
      console.error('Error creating landing page:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to create landing page');
      }
      return null;
    }
  }, [checkAuth, navigate]);

  const updateLandingPage = useCallback(async (id, data) => {
    if (!checkAuth()) return null;
    try {
      const response = await LandingPagesApi.update(id, data);
      
      if (response.status === 204 || response.status === 200 || response.data?.success) {
        return response.data?.landing_page || response.data || true;
      }
      return null;
    } catch (error) {
      console.error('Error updating landing page:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to update landing page');
      }
      return null;
    }
  }, [checkAuth, navigate]);

  const uploadLandingAsset = useCallback(async (file) => {
    if (!checkAuth()) return null;
    try {
      const response = await LandingPagesApi.upload(file);
      return response.data;
    } catch (error) {
      console.error('Error uploading landing page asset:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to upload asset');
      }
      return null;
    }
  }, [checkAuth, navigate]);

  const deleteLandingPage = useCallback(async (id) => {
    if (!checkAuth()) return false;
    try {
      await LandingPagesApi.destroy(id);
      message.success('Landing page deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting landing page:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to delete landing page');
      }
      return false;
    }
  }, [checkAuth, navigate]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    const params = {
      page: currentPage,
      per: pageSize,
    };
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params[key] = value;
      }
    });
    paramsRef.current = params;
    fetchLandingPages(params);
  }, [currentPage, pageSize, filters, fetchLandingPages]);

  const updateFilters = useCallback((newFilters) => {
    if (Object.keys(newFilters).length === 0) {
      setFilters({});
    } else {
      setFilters(prev => ({ ...prev, ...newFilters }));
    }
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  return {
    landingPages,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    shoppingCenters,
    loadingCompanies,
    updateFilters,
    handlePageChange,
    deleteLandingPage,
    createLandingPage,
    updateLandingPage,
    fetchLandingPage,
    uploadLandingAsset,
    refetch: () => fetchLandingPages(paramsRef.current),
  };
};

export default useLandingPages;


